import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { signIn } from '../../Module/Auth/Presentation/authActions';
import { selectAuthState } from '../../Module/Auth/Presentation/authSelectors';
import { PATHS } from '../../routes/paths';
import { getAccessToken } from '../../core/api/tokenStorage';

const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
const DEFAULT_KAKAO_DONOR_REDIRECT_URL = 'https://missionfund.org/login_ok_kakaouser.asp';

declare global {
  interface Window {
    Kakao: any;
  }
}

export const useLogin = () => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, error } = useAppSelector(selectAuthState);

  // If already logged in (userId exists), skip login screen
  useEffect(() => {
    try {
      const userId = window.localStorage.getItem('missionfund.userId');
      const accessToken = getAccessToken();
      if (userId && accessToken) navigate(PATHS.PROJECT_MANAGEMENT, { replace: true });
    } catch {
      // ignore
    }
  }, [navigate]);

  const [loginType, setLoginType] = useState<'fundraiser' | 'donor'>(() => {
    if (typeof window === 'undefined') return 'fundraiser';
    const params = new URLSearchParams(window.location.search);
    return params.get('loginType') === 'donor' ? 'donor' : 'fundraiser';
  });
  const [memberType, setMemberType] = useState<'individual' | 'group'>('individual');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const kakaoSdkLoadingRef = useRef<Promise<any> | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('loginType') === 'donor') {
      setLoginType('donor');
    }
  }, [location.search]);

  const ensureKakaoSdk = useCallback(async () => {
    if (window.Kakao?.Auth) {
      if (!window.Kakao.isInitialized()) {
        window.Kakao.init(KAKAO_KEY);
      }
      return window.Kakao;
    }

    if (kakaoSdkLoadingRef.current) {
      return kakaoSdkLoadingRef.current;
    }

    kakaoSdkLoadingRef.current = new Promise((resolve, reject) => {
      const finalize = () => {
        if (!window.Kakao?.Auth) {
          reject(new Error('Kakao SDK not available'));
          return;
        }
        if (!window.Kakao.isInitialized()) {
          window.Kakao.init(KAKAO_KEY);
        }
        resolve(window.Kakao);
      };

      const sdkSrc = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
      const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${sdkSrc}"]`);
      if (existingScript) {
        if (window.Kakao?.Auth) {
          finalize();
          return;
        }
        existingScript.addEventListener('load', finalize, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Kakao SDK script')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = sdkSrc;
      script.crossOrigin = 'anonymous';
      script.onload = finalize;
      script.onerror = () => reject(new Error('Failed to load Kakao SDK script'));
      document.head.appendChild(script);
    }).catch((error) => {
      kakaoSdkLoadingRef.current = null;
      throw error;
    });

    return kakaoSdkLoadingRef.current;
  }, []);

  const handleKakaoDonorLogin = useCallback(async () => {
    const configuredRedirectUrl = (process.env.REACT_APP_KAKAO_DONOR_REDIRECT_URL as string | undefined)?.trim();
    const redirectUrl = configuredRedirectUrl || DEFAULT_KAKAO_DONOR_REDIRECT_URL;

    try {
      const kakao = await ensureKakaoSdk();
      kakao.Auth.authorize({ redirectUri: redirectUrl });
    } catch (error) {
      console.error('Failed to start Kakao authorize flow:', error);
      window.location.assign(
        `https://kauth.kakao.com/oauth/authorize?client_id=${encodeURIComponent(KAKAO_KEY)}&redirect_uri=${encodeURIComponent(redirectUrl)}&response_type=code`,
      );
    }
  }, [ensureKakaoSdk]);

  const handleLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const partner = (process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF';
      const res = await dispatch(signIn({ user_email: email, password, partner })).unwrap();
      if (res) {
        navigate(PATHS.PROJECT_MANAGEMENT);
      }
    },
    [dispatch, email, navigate, password]
  );

  const isBusy = status === 'loading';

  const getFriendlyErrorMessage = (err: string) => {
    if (!err) return '';
    if (err.includes('User not found') || err.includes('Incorrect password')) {
      return t('page.login.errors.invalidCredentials');
    }
    if (err.includes('Network Error')) {
      return t('page.login.errors.network');
    }
    return t('page.login.errors.default');
  };

  return {
    loginType,
    setLoginType,
    memberType,
    setMemberType,
    email,
    setEmail,
    password,
    setPassword,
    handleLogin,
    handleKakaoDonorLogin,
    isBusy,
    error: getFriendlyErrorMessage(error || ''),
  };
};

